import { useState } from "react";
import { api, ApiError } from "../lib/api";

interface AIAssistProps {
  // What kind of copy to generate (matches the backend prompt map).
  kind: "bio" | "hero_tagline" | "about_long" | "service_card" | "statement" | "callout_hook";
  // Photographer / portfolio context that primes the prompt.
  portfolioName: string;
  style?: string;
  keywords?: string;
  // Insertion callback — fired with the generated text.
  onAccept: (text: string) => void;
  // Optional CTA label override.
  label?: string;
}

export function AIAssist({ kind, portfolioName, style, keywords, onAccept, label }: AIAssistProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      const { text } = await api.aiGenerate(kind, {
        portfolio_name: portfolioName,
        style: style ?? "",
        keywords: keywords ?? "",
      });
      onAccept(text);
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 503) {
        setDisabled(true);
        setError("AI isn't configured in this deployment yet.");
      } else if (e instanceof ApiError && e.status === 409) {
        setError("Rate limit (10/hour) reached — try again later.");
      } else {
        setError(e instanceof Error ? e.message : "Generation failed");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
      <button
        type="button"
        className="btn-ghost"
        style={{ fontSize: "0.8rem", padding: "0.2rem 0.55rem" }}
        disabled={busy || disabled}
        onClick={() => void generate()}
        title={disabled ? "Configure OpenRouter to enable" : "Generate a suggestion with AI"}
      >
        {busy ? "…" : "✨ "}{label ?? "Suggest with AI"}
      </button>
      {error && <small style={{ color: "var(--muted)", fontSize: "0.75rem" }}>{error}</small>}
    </div>
  );
}
